import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  tUnsavedChanges,
  type UnsavedChangesGuardStringKey,
} from "@/i18n/unsavedChangesStrings";

interface UnsavedChangesDialogProps {
  open: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  /**
   * When true, the Discard action is disabled and the prompt cannot be
   * dismissed by clicking outside / pressing Esc. Wire this to the underlying
   * save mutation's pending state to prevent mid-request discards.
   */
  isSaving?: boolean;
  /**
   * Optional per-call i18n overrides for the title, description, and button
   * labels. Keys you omit fall back to the global registry, then to English.
   * Typically forwarded from `useUnsavedChangesGuard({ i18n })`.
   */
  i18n?: Partial<Record<UnsavedChangesGuardStringKey, string>>;
  /**
   * Optional surface label so the dialog can resolve copy from a registered
   * per-surface preset (see `registerUnsavedChangesSurfacePreset`). Typically
   * forwarded from `useUnsavedChangesGuard({ surface })`.
   */
  surface?: string;
  /**
   * Optional override for the in-flight save warning shown in place of the
   * default description while `isSaving` is true. Falls back to an English
   * default if not provided.
   */
  savingDescription?: string;
}

const DEFAULT_SAVING_DESCRIPTION =
  "A save is in progress. Please wait for it to finish before discarding.";

export const UnsavedChangesDialog = ({
  open,
  onCancel,
  onDiscard,
  isSaving = false,
  i18n,
  surface,
  savingDescription = DEFAULT_SAVING_DESCRIPTION,
}: UnsavedChangesDialogProps) => (
  <AlertDialog
    open={open}
    onOpenChange={(o) => {
      if (isSaving) return; // lock the prompt while a save is in flight
      if (!o) onCancel();
    }}
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {tUnsavedChanges("discardDialogTitle", i18n, surface)}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {isSaving
            ? savingDescription
            : tUnsavedChanges("discardDialogDescription", i18n, surface)}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={onCancel} disabled={isSaving}>
          {tUnsavedChanges("discardDialogCancel", i18n, surface)}
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={onDiscard}
          disabled={isSaving}
          aria-disabled={isSaving}
        >
          {tUnsavedChanges("discardDialogConfirm", i18n, surface)}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
